/**
 * Shared Filter Utilities
 * 
 * Common icons, constants, and filter functions used by:
 * - FilterDropdown (Flow view)
 * - KanbanFilterDropdown (Kanban view)
 * - useKanbanData hook
 */

import { isStatusInProgress, isStatusNotStarted, isStatusBlocked } from '../FlowchartView/utils/statusUtils';
import {
  isNestedSprintCompleted,
  isNestedSprintInProgress,
  isTaskBlocked,
} from './utils/taskAvailabilityUtils';

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

export const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

export const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const QuestMarkerIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const ActiveMarkerIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const NoResultsIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// FILTER OPTIONS
// ═══════════════════════════════════════════════════════════════

export const PRIORITY_OPTIONS = [
  { id: 'high', label: 'High', color: '#ef4444' },
  { id: 'medium', label: 'Medium', color: '#f59e0b' },
  { id: 'low', label: 'Low', color: '#22c55e' },
  { id: 'none', label: 'None', color: '#6b7280' },
];

export const DIFFICULTY_OPTIONS = [
  { id: 'very_easy', label: 'Very Easy', color: '#22c55e' },
  { id: 'easy', label: 'Easy', color: '#84cc16' },
  { id: 'medium', label: 'Medium', color: '#f59e0b' },
  { id: 'hard', label: 'Hard', color: '#f97316' },
  { id: 'extreme', label: 'Extreme', color: '#ef4444' },
  { id: 'none', label: 'None', color: '#6b7280' },
];

export const DUE_DATE_OPTIONS = [
  { id: 'overdue', label: 'Overdue', color: '#ef4444' },
  { id: 'today', label: 'Due Today', color: '#f59e0b' },
  { id: 'this-week', label: 'Due This Week', color: '#3b82f6' },
  { id: 'no-date', label: 'No Due Date', color: '#6b7280' },
];

// Custom field filter options for checkbox type
export const CHECKBOX_FILTER_OPTIONS = [
  { id: 'checked', label: 'Checked', color: '#22c55e' },
  { id: 'unchecked', label: 'Unchecked', color: '#6b7280' },
];

// Custom field filter options for URL type
export const URL_FILTER_OPTIONS = [
  { id: 'has-url', label: 'Has URL', color: '#3b82f6' },
  { id: 'no-url', label: 'No URL', color: '#6b7280' },
];

// Custom field filter options for date type (reuses due date logic)
export const CUSTOM_DATE_FILTER_OPTIONS = [
  { id: 'overdue', label: 'Past', color: '#ef4444' },
  { id: 'today', label: 'Today', color: '#f59e0b' },
  { id: 'this-week', label: 'This Week', color: '#3b82f6' },
  { id: 'has-date', label: 'Has Date', color: '#22c55e' },
  { id: 'no-date', label: 'No Date', color: '#6b7280' },
];

export const MISSION_TYPE_OPTIONS = [
  { id: 'available', label: 'Available Missions', color: '#f59e0b', icon: QuestMarkerIcon },
  { id: 'active', label: 'Active Missions', color: '#22c55e', icon: ActiveMarkerIcon },
];

// ═══════════════════════════════════════════════════════════════
// FILTER MATCHING FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Check if a date falls into a due date category
 */
export const matchesDueDateFilter = (dueDate, filterId) => {
  // Handle 'has-date' filter (for custom date fields)
  if (filterId === 'has-date') {
    return !!dueDate;
  }
  if (filterId === 'no-date') {
    return !dueDate;
  }
  
  if (!dueDate) return false;
  
  const due = new Date(dueDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  // End of this week (Sunday)
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
  
  switch (filterId) {
    case 'overdue':
      return due < today;
    case 'today':
      return due >= today && due < tomorrow;
    case 'this-week':
      return due >= today && due <= endOfWeek;
    default:
      return false;
  }
};

/**
 * Check if a task matches mission type filter (available/active)
 * Uses the same logic as FlowchartHUD for consistency
 */
export const matchesMissionTypeFilter = (task, filterId, lockStates, nestedSprints, statuses, allSprints = []) => {
  if (task.nodeType === 'milestone' || task.nodeType === 'note') return false;

  switch (filterId) {
    case 'available':
      if (isNestedSprintCompleted(task, nestedSprints)) return false;
      if (isNestedSprintInProgress(task, nestedSprints, statuses, allSprints)) return false;
      if (isTaskBlocked(task, nestedSprints, statuses, allSprints)) return false;

      if (lockStates[task.id]?.isLocked) return false;
      if (!isStatusNotStarted(task.status, statuses)) return false;
      if (task.nodeType === 'decision' && task.answer) return false;
      return true;
      
    case 'active':
      if (isNestedSprintCompleted(task, nestedSprints)) return false;
      if (isNestedSprintInProgress(task, nestedSprints, statuses, allSprints)) return true;
      if (isStatusInProgress(task.status, statuses)) return true;
      if (isStatusBlocked(task.status, statuses)) return true;
      if (isTaskBlocked(task, nestedSprints, statuses, allSprints)) return true;
      return false;
      
    default:
      return false;
  }
};

// ═══════════════════════════════════════════════════════════════
// CUSTOM FIELD FILTER MATCHING
// ═══════════════════════════════════════════════════════════════

/**
 * Check if a task's custom field value matches the filter criteria
 * 
 * @param {any} value - The task's custom field value
 * @param {Object} field - The custom field definition
 * @param {Object} filterCriteria - The filter criteria for this field
 * @returns {boolean} - Whether the value matches the filter
 */
export const matchesCustomFieldFilter = (value, field, filterCriteria) => {
  if (!field || !filterCriteria) return true;
  
  const { type } = field;
  
  switch (type) {
    case 'select':
      // Multi-select: match if value is in selected options
      if (!filterCriteria.values?.length) return true;
      return filterCriteria.values.includes(value);
      
    case 'checkbox':
      // Checkbox: match checked/unchecked state
      if (!filterCriteria.values?.length) return true;
      const isChecked = Boolean(value);
      if (filterCriteria.values.includes('checked') && isChecked) return true;
      if (filterCriteria.values.includes('unchecked') && !isChecked) return true;
      return false;
      
    case 'text':
      // Text: contains search (case-insensitive)
      if (!filterCriteria.search) return true;
      if (!value) return false;
      return String(value).toLowerCase().includes(filterCriteria.search.toLowerCase());
      
    case 'number':
      // Number: comparison operators
      if (!filterCriteria.operator || filterCriteria.value === null || filterCriteria.value === undefined || filterCriteria.value === '') return true;
      const numValue = Number(value);
      const filterNum = Number(filterCriteria.value);
      if (isNaN(numValue)) return false;
      switch (filterCriteria.operator) {
        case 'eq': return numValue === filterNum;
        case 'neq': return numValue !== filterNum;
        case 'gt': return numValue > filterNum;
        case 'gte': return numValue >= filterNum;
        case 'lt': return numValue < filterNum;
        case 'lte': return numValue <= filterNum;
        default: return true;
      }
      
    case 'date':
      // Date: reuse due date filter logic
      if (!filterCriteria.values?.length) return true;
      return filterCriteria.values.some(filterId => matchesDueDateFilter(value, filterId));
      
    case 'url':
      // URL: has/no URL
      if (!filterCriteria.values?.length) return true;
      const hasUrl = value && String(value).trim().length > 0;
      if (filterCriteria.values.includes('has-url') && hasUrl) return true;
      if (filterCriteria.values.includes('no-url') && !hasUrl) return true;
      return false;
      
    default:
      return true;
  }
};

/**
 * Check if a task matches all custom field filters
 * 
 * @param {Object} task - The task to check
 * @param {Object} customFieldFilters - Object mapping fieldId to filter criteria
 * @param {Array} customFields - Array of custom field definitions
 * @returns {boolean} - Whether the task matches all custom field filters
 */
export const matchesAllCustomFieldFilters = (task, customFieldFilters, customFields) => {
  if (!customFieldFilters || Object.keys(customFieldFilters).length === 0) return true;
  if (!customFields || customFields.length === 0) return true;
  
  const taskValues = task.customFieldValues || {};
  
  for (const [fieldId, filterCriteria] of Object.entries(customFieldFilters)) {
    // Skip empty filter criteria
    if (!filterCriteria) continue;
    if (filterCriteria.values?.length === 0 && !filterCriteria.search && filterCriteria.value === undefined) continue;
    
    const field = customFields.find(f => f.id === fieldId);
    if (!field) continue;
    
    const value = taskValues[fieldId];
    if (!matchesCustomFieldFilter(value, field, filterCriteria)) {
      return false;
    }
  }
  
  return true;
};

/**
 * Count active custom field filters
 * @param {Object} customFieldFilters - Object mapping fieldId to filter criteria
 * @returns {number} - Count of active filters
 */
export const countCustomFieldFilters = (customFieldFilters) => {
  if (!customFieldFilters) return 0;
  
  let count = 0;
  for (const filterCriteria of Object.values(customFieldFilters)) {
    if (!filterCriteria) continue;
    // Count based on filter type
    if (filterCriteria.values?.length > 0) count++;
    else if (filterCriteria.search) count++;
    else if (filterCriteria.value !== undefined && filterCriteria.value !== '' && filterCriteria.operator) count++;
  }
  return count;
};

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Get unique categories from tasks with colors
 */
export const getTaskCategories = (tasks, categories) => {
  const categorySet = new Set();
  tasks.forEach(task => {
    if (task.category) categorySet.add(task.category);
  });
  return Array.from(categorySet).map(catName => {
    const found = categories.find(c => c.name === catName);
    return {
      name: catName,
      color: found?.color || '#6b7280',
    };
  });
};

/**
 * Get unique tags from tasks with colors
 */
export const getTaskTags = (tasks, allTags) => {
  const tagSet = new Set();
  tasks.forEach(task => {
    if (task.tags) {
      task.tags.forEach(tag => tagSet.add(tag));
    }
  });
  return Array.from(tagSet).map(tagName => {
    const found = allTags.find(t => t.name === tagName);
    return {
      name: tagName,
      color: found?.color || '#6b7280',
    };
  });
};

/**
 * Apply filters to tasks
 * Shared filter logic used by Kanban, Table, Gantt, and Flow views
 * 
 * @param {Array} tasks - Tasks to filter
 * @param {Object} filters - Filter criteria
 * @param {boolean} showArchived - Whether to show archived tasks
 * @param {Object} lockStates - Lock states for actionable calculation
 * @param {Array} nestedSprints - Nested sprints for actionable calculation
 * @param {Array} statuses - Status configurations
 * @param {boolean} showActionableOnly - Whether to only show actionable tasks
 * @param {Array} customFields - Custom field definitions (optional, for custom field filtering)
 */
export const applyFilters = (tasks, filters, showArchived = false, lockStates = {}, nestedSprints = [], statuses = [], showActionableOnly = false, customFields = []) => {
  const kept = tasks.filter(task => {
    if (task.nodeType === 'note') {
      if (!showArchived && task.archived) return false;
      if (showArchived && !task.archived) return false;
      return true;
    }
    // ALWAYS filter out archived tasks unless explicitly showing them
    if (!showArchived && task.archived) return false;
    
    // If showing archived, ONLY show archived tasks
    if (showArchived && !task.archived) return false;
    
    // Actionable only filter - task must be available OR active
    if (showActionableOnly) {
      const isAvailable = matchesMissionTypeFilter(task, 'available', lockStates, nestedSprints, statuses);
      const isActive = matchesMissionTypeFilter(task, 'active', lockStates, nestedSprints, statuses);
      if (!isAvailable && !isActive) return false;
    }
    
    // Apply other filters only if they exist
    if (!filters || Object.keys(filters).length === 0) return true;
    
    // Check if any filters are actually set (including custom fields)
    const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
      if (key === 'customFields') {
        return countCustomFieldFilters(value) > 0;
      }
      return Array.isArray(value) && value.length > 0;
    });
    if (!hasActiveFilters) return true;
    
    // Mission type filter (available/active)
    if (filters.missionType?.length > 0) {
      if (!filters.missionType.some(filterId => matchesMissionTypeFilter(task, filterId, lockStates, nestedSprints, statuses))) return false;
    }
    
    // Status filter
    if (filters.status?.length > 0 && !filters.status.includes(task.status)) return false;
    
    // Category filter
    if (filters.category?.length > 0 && (!task.category || !filters.category.includes(task.category))) return false;
    
    // Tag filter (matches if task has ANY of the selected tags)
    if (filters.tags?.length > 0 && (!task.tags || !filters.tags.some(tag => task.tags.includes(tag)))) return false;
    
    // Due date filter
    if (filters.dueDate?.length > 0 && !filters.dueDate.some(filterId => matchesDueDateFilter(task.dueDate, filterId))) return false;
    
    // Priority filter
    if (filters.priority?.length > 0 && !filters.priority.includes(task.priority || 'none')) return false;
    
    // Difficulty filter
    if (filters.difficulty?.length > 0 && !filters.difficulty.includes(task.difficulty || 'none')) return false;
    
    // Custom field filters
    if (filters.customFields && customFields.length > 0) {
      if (!matchesAllCustomFieldFilters(task, filters.customFields, customFields)) return false;
    }
    
    return true;
  });

  const visibleIds = new Set(kept.filter((t) => t.nodeType !== 'note').map((t) => t.id));
  return kept.filter((t) => {
    if (t.nodeType !== 'note') return true;
    const hostId = t.noteAnchor?.hostTaskId;
    return hostId && visibleIds.has(hostId);
  });
}; 